/**
 * Helpers for building SSR <head> markup. Shared between the marketing
 * SSR renderer and the tenant SSR renderer (entry-server.tsx).
 */

import { EXPLORE_LOCALES, MARKETING_LANG_CODES } from "i18n/urlLocale";

export function escapeHtml(str: string): string {
	return str
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

/**
 * Serialise one or more JSON-LD schema objects into a <script> tag. Escapes
 * </script> sequences to prevent XSS injection through user-controlled fields.
 */
export function jsonLdScriptTag(schemas: object | object[]): string {
	const arr = Array.isArray(schemas) ? schemas : [schemas];
	if (arr.length === 0) return "";
	// One <script> tag per schema — Google recommends this over bare JSON arrays
	return arr
		.map((schema) => {
			const json = JSON.stringify(schema).replace(/<\/script>/gi, "<\\/script>");
			return `<script type="application/ld+json" data-cfasync="false">${json}</script>`;
		})
		.join("\n\t");
}

export interface CommonHeadOpts {
	title: string;
	description: string;
	canonical: string;
	ogImage: string;
	ogImageWidth?: number;
	ogImageHeight?: number;
	ogType?: string;
	siteName?: string;
	/**
	 * Overrides the default `index, follow`. Set it on any page whose right to be indexed is
	 * conditional — the /discover guides, where 4,352 of 4,377 geos are thin — because the
	 * default here is not a neutral omission but an active instruction to index.
	 */
	robots?: string;
}

/**
 * Build the standard set of <title> + <meta> tags shared across all marketing
 * pages: title, description, canonical, OG, Twitter.
 */
export function buildCommonHead(opts: CommonHeadOpts): string {
	const title = escapeHtml(opts.title);
	const description = escapeHtml(opts.description);
	const canonical = escapeHtml(opts.canonical);
	const ogImage = escapeHtml(opts.ogImage);
	const ogType = opts.ogType || "website";
	const siteName = opts.siteName ? escapeHtml(opts.siteName) : "Stippl";

	return [
		`<title>${title}</title>`,
		`<meta name="description" content="${description}" />`,
		`<link rel="canonical" href="${canonical}" />`,
		`<meta name="robots" content="${escapeHtml(opts.robots || "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1")}" />`,
		`<meta name="format-detection" content="telephone=no" />`,
		`<meta property="og:site_name" content="${siteName}" />`,
		`<meta property="og:type" content="${ogType}" />`,
		`<meta property="og:title" content="${title}" />`,
		`<meta property="og:description" content="${description}" />`,
		`<meta property="og:image" content="${ogImage}" />`,
		...(opts.ogImageWidth ? [`<meta property="og:image:width" content="${opts.ogImageWidth}" />`] : []),
		...(opts.ogImageHeight ? [`<meta property="og:image:height" content="${opts.ogImageHeight}" />`] : []),
		`<meta property="og:url" content="${canonical}" />`,
		`<meta name="twitter:card" content="summary_large_image" />`,
		`<meta name="twitter:title" content="${title}" />`,
		`<meta name="twitter:description" content="${description}" />`,
		`<meta name="twitter:image" content="${ogImage}" />`,
	].join("\n\t");
}

/**
 * Build hreflang <link rel="alternate"> tags for the blog list page.
 *
 * All locales share the same "/blog" path, so a simple locale-prefix loop
 * is sufficient. x-default points to the English (no-prefix) URL.
 *
 * @param baseUrl  e.g. "https://www.stippl.io"
 * @param availableLocales  locales that have published content, e.g. ["en", "nl", "de"]
 */
/**
 * hreflang for the /discover hub and its six locale siblings.
 *
 * The hub exists at a fixed path in all seven locales — unlike the blog article cluster, whose
 * slugs differ per locale, and unlike the blog list, where a locale can be absent — so the set is
 * not data-dependent and there is nothing to pass in.
 *
 * Not optional. 123yb6937h0 made all seven of these indexable at once (the bare index used to
 * carry `noindex`), and seven near-identical pages with no reciprocal annotation is the shape
 * Google resolves by picking one and dropping the rest.
 */
export function buildDiscoverHubHreflang(baseUrl: string): string {
	const enUrl = escapeHtml(`${baseUrl}/discover`);
	const lines = ["en", "nl", "de", "fr", "es", "it", "pt"].map((locale) => {
		const prefix = locale === "en" ? "" : `/${locale}`;
		return `<link rel="alternate" hreflang="${escapeHtml(locale)}" href="${escapeHtml(`${baseUrl}${prefix}/discover`)}" />`;
	});
	lines.push(`<link rel="alternate" hreflang="x-default" href="${enUrl}" />`);
	return lines.join("\n\t");
}

export function buildBlogListHreflang(baseUrl: string, availableLocales: string[]): string {
	if (!availableLocales || availableLocales.length === 0) return "";

	const enUrl = escapeHtml(`${baseUrl}/blog`);
	const lines: string[] = [];

	for (const locale of availableLocales) {
		const prefix = locale === "en" ? "" : `/${locale}`;
		const href = escapeHtml(`${baseUrl}${prefix}/blog`);
		lines.push(`<link rel="alternate" hreflang="${escapeHtml(locale)}" href="${href}" />`);
	}
	lines.push(`<link rel="alternate" hreflang="x-default" href="${enUrl}" />`);

	return lines.join("\n\t");
}

/**
 * Build hreflang <link rel="alternate"> tags for a blog article page.
 *
 * Each locale may have a different slug, so we accept a locale→slug map.
 * Only emits alternates for locales present in the map (avoids 404-pointing tags).
 * x-default points to the English (no-prefix) URL.
 *
 * @param baseUrl      e.g. "https://www.stippl.io"
 * @param localeSlugs  map of locale → slug, e.g. { en: "best-paris-tips", nl: "beste-parijs-tips" }
 */
export function buildBlogHreflang(baseUrl: string, localeSlugs: Record<string, string>): string {
	const entries = Object.entries(localeSlugs).filter(([, slug]) => slug);
	if (entries.length === 0) return "";

	const enSlug = localeSlugs["en"];
	const enUrl = enSlug ? escapeHtml(`${baseUrl}/blog/${enSlug}`) : null;
	const lines: string[] = [];

	for (const [locale, slug] of entries) {
		const prefix = locale === "en" ? "" : `/${locale}`;
		const href = escapeHtml(`${baseUrl}${prefix}/blog/${slug}`);
		lines.push(`<link rel="alternate" hreflang="${escapeHtml(locale)}" href="${href}" />`);
	}
	if (enUrl) lines.push(`<link rel="alternate" hreflang="x-default" href="${enUrl}" />`);

	return lines.join("\n\t");
}

/**
 * hreflang tags for a /discover guide page.
 *
 * Simpler than the blog's equivalent because Explore keeps `slug` canonical English — names
 * and prose localise, the lookup key does not — so every locale of a geo lives at the same
 * path under a different prefix. Restricted to EXPLORE_LOCALES rather than MARKETING_LANG_CODES:
 * the two are equal today, but if a future marketing locale gets added before Explore has
 * content for it, pointing an alternate at e.g. /it/discover/thailand for that locale would
 * advertise a page whose content is still English.
 *
 * @param geo  "thailand" or "thailand/bangkok"
 */
export function buildDiscoverHreflang(baseUrl: string, geo: string): string {
	if (!geo) return "";
	const lines = EXPLORE_LOCALES.map((locale) => {
		const prefix = locale === "en" ? "" : `/${locale}`;
		return `<link rel="alternate" hreflang="${locale}" href="${escapeHtml(`${baseUrl}${prefix}/discover/${geo}`)}" />`;
	});
	lines.push(`<link rel="alternate" hreflang="x-default" href="${escapeHtml(`${baseUrl}/discover/${geo}`)}" />`);
	return lines.join("\n\t");
}

/**
 * hreflang tags for the homepage.
 *
 * Uses `MARKETING_LANG_CODES` rather than `EXPLORE_LOCALES` (the list `buildDiscoverHreflang`
 * restricts to) on purpose, even though the two are equal today: the homepage has hand-written,
 * hand-translated copy in every marketing language regardless of whether Explore has content in
 * that locale, so there is no thin-content risk in advertising all 7 as alternates — unlike
 * /discover, which must stay tied to Explore's actual coverage.
 *
 * @param baseUrl  e.g. "https://www.stippl.io"
 */
export function buildHomeHreflang(baseUrl: string): string {
	const lines = MARKETING_LANG_CODES.map((locale) => {
		const prefix = locale === "en" ? "" : `/${locale}`;
		return `<link rel="alternate" hreflang="${locale}" href="${escapeHtml(`${baseUrl}${prefix}`)}" />`;
	});
	lines.push(`<link rel="alternate" hreflang="x-default" href="${escapeHtml(baseUrl)}" />`);
	return lines.join("\n\t");
}

/**
 * hreflang tags for a graduated feature page (phase 3 step 2 of
 * work/locale-coverage-unification.md) — English plus only the locales that have actually
 * graduated for this path (their own hand-written title/meta AND a fully translated body; see
 * `FEATURE_PAGE_LOCALE_META` / `FEATURE_PAGES_WITH_LOCALISED_BODY` in
 * `marketing/content/featurePageLocaleMeta.ts`).
 *
 * Unlike `buildHomeHreflang`, this can't just loop over every marketing locale: most locales of
 * most feature pages are still the `noindex, follow` placeholder from phase 3 step 1, and
 * hreflang pointing at a noindex page is an invalid, self-contradicting annotation Google
 * discards the whole cluster for. Callers pass only the locales that have graduated.
 *
 * @param path              one of LOCALISED_FEATURE_PATHS, e.g. "/scratch-map"
 * @param graduatedLocales  the non-English locales that have graduated for this path
 */
export function buildFeatureHreflang(baseUrl: string, path: string, graduatedLocales: readonly string[]): string {
	const enUrl = escapeHtml(`${baseUrl}${path}`);
	const lines = ["en", ...graduatedLocales].map((locale) => {
		const prefix = locale === "en" ? "" : `/${locale}`;
		return `<link rel="alternate" hreflang="${escapeHtml(locale)}" href="${escapeHtml(`${baseUrl}${prefix}${path}`)}" />`;
	});
	lines.push(`<link rel="alternate" hreflang="x-default" href="${enUrl}" />`);
	return lines.join("\n\t");
}
